if (!customElements.get('mdv-image-slider')) {
  customElements.define(
    'mdv-image-slider',
    class MDVImageSlider extends HTMLElement {
      connectedCallback() {
        if (this._initObserver || this.swiper) return;
        var rect = this.getBoundingClientRect();
        var nearViewport = rect.top < window.innerHeight + 200 && rect.bottom > -200;
        if (nearViewport || !('IntersectionObserver' in window)) {
          this.init();
          return;
        }
        this._initObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.init();
              this._initObserver.disconnect();
            }
          });
        }, { rootMargin: '200px 0px', threshold: 0 });
        this._initObserver.observe(this);
      }

      init() {
        const spaceBetween = parseInt(this.dataset.space) || 0;

        this.swiper = new Swiper(this.querySelector('.swiper'), {
          slidesPerView: this.dataset.mobile || 1,
          loop: false,
          spaceBetween: parseInt(spaceBetween * 0.5) || 0,
          centeredSlides: false,
          observer: true,
          observeParents: true,
          resizeObserver: true,
          breakpoints: {
            768: {
              slidesPerView: this.dataset.tablet || 2,
              spaceBetween: parseInt(spaceBetween * 0.7) || 0,
            },
            1024: {
              slidesPerView: this.dataset.desktop || 3,
              spaceBetween: spaceBetween,
            },
          },
          on: {
            init: () => this.initVideoSlides(),
            observerUpdate: () => this.initVideoSlides(),
          },
        });

        this.initVideoSlides();
      }

      // ─── Schedule heavy work outside the current interaction (INP-safe) ──────
      // Priority ladder: scheduler.postTask → requestIdleCallback → setTimeout
      _defer(fn) {
        if (typeof scheduler !== 'undefined' && scheduler.postTask) {
          scheduler.postTask(fn, { priority: 'background' });
        } else if (typeof requestIdleCallback !== 'undefined') {
          requestIdleCallback(fn, { timeout: 2000 });
        } else {
          setTimeout(fn, 0);
        }
      }

      // ─── Attach listeners; strip data-src so browser never auto-fetches ──────
      initVideoSlides() {
        this.querySelectorAll('[data-mdv-video]').forEach((wrapper) => {
          if (wrapper.dataset.mdvVideoInit === 'true') return;
          wrapper.dataset.mdvVideoInit = 'true';

          const video = wrapper.querySelector('video');
          if (!video) return;

          // Stash real src from data-src, remove attribute — zero network on load
          const realSrc = video.getAttribute('data-src') || '';
          video.removeAttribute('data-src');
          wrapper.dataset.mdvSrc = realSrc;

          // Native video events keep the class in sync
          video.addEventListener('play',  () => wrapper.classList.add('video-playing'));
          video.addEventListener('pause', () => wrapper.classList.remove('video-playing'));
          video.addEventListener('ended', () => wrapper.classList.remove('video-playing'));

          wrapper.addEventListener('click', () => {
            // Pause path: synchronous so interaction commits immediately (INP)
            if (!video.paused && video.src && video.src !== window.location.href) {
              this.pauseVideo(wrapper, video);
              return;
            }

            // Play path: defer fetch/decode so INP frame stays short
            this._defer(() => {
              const hasSrc = video.src && video.src !== window.location.href;
              hasSrc
                ? this.playVideo(wrapper, video)
                : this._loadAndPlay(wrapper, video);
            });
          });
        });
      }

      // ─── Fetch video → Blob URL → assign → play ──────────────────────────────
      _loadAndPlay(wrapper, video) {
        const src = wrapper.dataset.mdvSrc;
        if (!src) return;

        wrapper.classList.add('video-loading');

        fetch(src)
          .then((res) => {
            if (!res.ok) throw new Error(`[mdv] fetch ${res.status}`);
            return res.blob();
          })
          .then((blob) => {
            // Revoke previous blob to free memory
            if (wrapper.dataset.mdvBlob) URL.revokeObjectURL(wrapper.dataset.mdvBlob);

            const blobUrl = URL.createObjectURL(blob);
            wrapper.dataset.mdvBlob = blobUrl;

            video.src = blobUrl;
            video.load();

            // Play only once browser has decoded enough frames (no blank-frame flash)
            const onCanPlay = () => {
              wrapper.classList.remove('video-loading');
              video.removeEventListener('canplay', onCanPlay);
              this.playVideo(wrapper, video);
            };
            video.addEventListener('canplay', onCanPlay);
          })
          .catch((err) => {
            wrapper.classList.remove('video-loading');
            console.warn('[mdv-image-slider] video load error:', err);
          });
      }

      // ─── Play: pause all siblings in this slider first ───────────────────────
      playVideo(wrapper, video) {
        this.querySelectorAll('[data-mdv-video]').forEach((w) => {
          if (w === wrapper) return;
          const v = w.querySelector('video');
          if (v && !v.paused) v.pause();
        });

        video.play().catch((err) => {
          console.warn('[mdv-image-slider] play() blocked:', err);
        });
      }

      // ─── Pause ───────────────────────────────────────────────────────────────
      pauseVideo(wrapper, video) {
        video.pause();
      }

      // ─── Cleanup: revoke Blob URLs + destroy Swiper on element removal ────────
      disconnectedCallback() {
        if (this._initObserver) this._initObserver.disconnect();
        this.querySelectorAll('[data-mdv-video]').forEach((wrapper) => {
          if (wrapper.dataset.mdvBlob) URL.revokeObjectURL(wrapper.dataset.mdvBlob);
        });
        if (this.swiper) this.swiper.destroy(true, true);
      }
    }
  );
}
